// The dashboard's vocabulary, bilingual: what each term means and why it is
// worth watching. Rendered by the Glossary view; the same wording is used by
// the panel explanations, so a reader meets one definition everywhere.

import type { Lang } from "@/i18n";

export interface GlossaryEntry {
  id: string;
  term: Record<Lang, string>;
  what: Record<Lang, string>;
  why: Record<Lang, string>;
}

export const GLOSSARY: readonly GlossaryEntry[] = [
  {
    id: "session",
    term: { de: "Session", en: "Session" },
    what: {
      de: "Ein Gespräch im Chat-Widget, vom Beginn (nach der Zustimmung ein gewählter Modus oder die erste Nachricht) bis zum Verlassen; derselbe Browser nutzt am selben Tag dieselbe Session weiter, auch über Unterseiten hinweg. Wer das Widget öffnet und nichts beginnt, erzeugt keine.",
      en: "One conversation in the chat widget, from its start (a chosen mode or the first message, after the consent) to leaving; the same browser keeps the same session on the same day, across subpages. Opening the widget and starting nothing creates none.",
    },
    why: {
      de: "Die Bezugsgrösse für Reichweite, Funnel und Kosten pro Kopf. Doppelt gezählte Sessions verfälschen jede Quote.",
      en: "The denominator for reach, funnel and cost per head. Double-counted sessions distort every rate.",
    },
  },
  {
    id: "turn",
    term: { de: "Turn", en: "Turn" },
    what: {
      de: "Eine Gesprächsdrehung: eine Frage der Nutzenden und die Antwort des Bots.",
      en: "One conversation turn: a user's question and the bot's answer.",
    },
    why: {
      de: "Die kleinste bepreiste Einheit — jeder Turn hat Token, ein Modell und Kosten. Turns pro Session zeigen die Tiefe der Nutzung.",
      en: "The smallest priced unit — every turn has tokens, a model and a cost. Turns per session show the depth of use.",
    },
  },
  {
    id: "question",
    term: { de: "Frage (question_count)", en: "Question (question_count)" },
    what: {
      de: "Die Anzahl gesendeter Nutzer-Nachrichten einer Session, vom Backend gezählt.",
      en: "The number of user messages sent in a session, counted by the backend.",
    },
    why: {
      de: "Definiert «vertieft» (≥3 Fragen) und «Absprung» (≤1 Frage) — die beiden wichtigsten Engagement-Schwellen.",
      en: "Defines “engaged” (≥3 questions) and “bounce” (≤1 question) — the two key engagement thresholds.",
    },
  },
  {
    id: "mode",
    term: { de: "Modus", en: "Mode" },
    what: {
      de: "Einer der Chat-Bereiche: FAQ, Beruf, Orientierung, Skillstrainer, Multicheck®. Jeder Modus hat einen eigenen System-Prompt und ein eigenes Modell.",
      en: "One of the chat areas: FAQ, Profession, Discovery, Skills trainer, Multicheck®. Each mode has its own system prompt and its own model.",
    },
    why: {
      de: "Modi werden pro Partner verkauft und laufen auf unterschiedlich teuren Modellen — der Modus ist die Einheit einer Rechnungsposition.",
      en: "Modes are sold per partner and run on models at different prices — the mode is the unit of an invoice item.",
    },
  },
  {
    id: "model",
    term: { de: "Modell", en: "Model" },
    what: {
      de: "Das Sprachmodell, das die Antwort erzeugt hat (Haiku 5.5, Sonnet 5.5, Opus 5, Gemini 3.8 Flash). Es wird pro Turn gespeichert.",
      en: "The language model that produced the answer (Haiku 5.5, Sonnet 5.5, Opus 5, Gemini 3.8 Flash). Stored per turn.",
    },
    why: {
      de: "Bestimmt den Tarif pro Token. Opus kostet rund 2,5× Sonnet — darum kann ein Modus mit weniger Token teurer sein.",
      en: "Determines the rate per token. Opus costs about 2.5× Sonnet — which is why a mode with fewer tokens can be more expensive.",
    },
  },
  {
    id: "tokens",
    term: { de: "Token (Input / Output)", en: "Tokens (input / output)" },
    what: {
      de: "Textbausteine, in denen Modelle rechnen (rund 4 Zeichen). Input = alles, was das Modell liest (Prompt, Kontext, Verlauf); Output = was es schreibt, inklusive Denk-Token.",
      en: "The text units models compute in (about 4 characters). Input = everything the model reads (prompt, context, history); output = what it writes, including thinking tokens.",
    },
    why: {
      de: "Kosten = Token × Tarif. Output-Token sind mehrfach teurer als Input-Token.",
      en: "Cost = tokens × rate. Output tokens are several times more expensive than input tokens.",
    },
  },
  {
    id: "cache",
    term: { de: "Cache-Hit / Cache gelesen / geschrieben", en: "Cache hit / cache read / write" },
    what: {
      de: "Claude speichert den System-Prompt eines Modus 1 Stunde zwischen, jeder Treffer verlängert die Frist. Ein Turn, der ihn wiederverwendet, ist ein Cache-Hit; Lesen kostet 10 % des Eingabe-Tarifs (Opus 5.5 und Sonnet 5.5: 5 %), Schreiben 200 %.",
      en: "Claude keeps a mode's system prompt cached for 1 hour, and every hit extends it. A turn that reuses it is a cache hit; reading costs 10 % of the input rate (Opus 5.5 and Sonnet 5.5: 5 %), writing 200 %.",
    },
    why: {
      de: "Der grösste Kostenhebel der Claude-Modi. Liegt das Lese/Schreib-Verhältnis unter dem Break-even im Kosten-Panel (etwa 1,1), kostet der Cache mehr, als er spart.",
      en: "The biggest cost lever of the Claude modes. A read/write ratio below the break-even in the cost panel (about 1,1) means the cache costs more than it saves.",
    },
  },
  {
    id: "partner",
    term: { de: "Partner (Mandant)", en: "Partner (tenant)" },
    what: {
      de: "Eine kaufmännische Partei, die das Widget einbettet. Ein Partner besitzt mehrere Origins (Hauptdomain, Subdomains, Staging), die zu einer Zeile zusammengefasst werden.",
      en: "A commercial party that embeds the widget. A partner owns several origins (main domain, subdomains, staging) that roll up into one row.",
    },
    why: {
      de: "Die Einheit für Kosten-, Qualitäts- und Reichweiten-Fragen im Vertrieb. «Ohne Origin» und «Nicht zugeordnet» halten die Summen vollständig.",
      en: "The unit for cost, quality and reach questions in sales. “No origin” and “Unassigned” keep the totals complete.",
    },
  },
  {
    id: "origin",
    term: { de: "Origin / einbettende Seite", en: "Origin / embedding host" },
    what: {
      de: "Die Web-Adresse der Seite, auf der das Widget geöffnet wurde (z.B. https://www.gateway.one). Seit 03.09.2026 meldet das Widget die Host-Seite; ältere Sessions stehen auf chat.gateway.one.",
      en: "The web address of the page on which the widget was opened (e.g. https://www.gateway.one). Since 03.09.2026 the widget reports the host page; older sessions read chat.gateway.one.",
    },
    why: {
      de: "Der Rohwert, aus dem die Partner-Zuordnung entsteht. Ein Origin ohne Zuordnung ist eine Konfigurationslücke.",
      en: "The raw value the partner mapping is derived from. An origin without a mapping is a configuration gap.",
    },
  },
  {
    id: "window",
    term: { de: "Fenster", en: "Window" },
    what: {
      de: "Der rückblickende Zeitraum eines Panels (24h, 7d, 30d, 3 Mte, 6 Mte, Max = 365 Tage), gerechnet ab jetzt.",
      en: "A panel's lookback period (24h, 7d, 30d, 3 mo, 6 mo, Max = 365 days), counted back from now.",
    },
    why: {
      de: "Alle Kennzahlen gelten nur für ihr Fenster. Sessions, die vor dem Fenster begannen, tragen keinen Partner («nicht zugeordnete Turns»).",
      en: "Every metric is valid only for its window. Sessions that started before the window carry no partner (“unattributed turns”).",
    },
  },
  {
    id: "test-session",
    term: { de: "Testsitzung", en: "Test session" },
    what: {
      de: "Eine intern markierte Session: ein Schlüsselwort beim Chatten, eine Vorschau der Testseite oder eine Frage des Fragen-Checks. Statistik und Kosten-Panels lassen sie weg, solange «Testsitzungen einbeziehen» im Kopf aus ist. Ihre Modellkosten zählen trotzdem zum Tagesbudget unter Betrieb, ausser bei den Fragen des Fragen-Checks.",
      en: "An internally flagged session: a keyword while chatting, a preview on the test page or a question of the question check. The statistics and the cost panels leave them out while «Include test sessions» in the header is off. Their model cost still counts toward the daily budget under Operations, except for the question check's questions.",
    },
    why: {
      de: "Hält interne Tests aus den Kennzahlen heraus, die Partner oder der Vorstand sehen. Das Tagesbudget begrenzt, was der Chat wirklich kostet, darum zählen sie dort mit.",
      en: "Keeps internal testing out of the figures partners or the board see. The daily budget bounds what the chat really costs, which is why they count there.",
    },
  },
  {
    id: "rag",
    term: { de: "RAG / Quellen / Kontext", en: "RAG / sources / context" },
    what: {
      de: "Retrieval-Augmented Generation: vor jeder Antwort sucht das System passende Seiten (gateway.one, Wiki, Berufe, junior) und gibt sie dem Modell als Kontext mit.",
      en: "Retrieval-augmented generation: before every answer the system searches fitting pages (gateway.one, wiki, professions, junior) and hands them to the model as context.",
    },
    why: {
      de: "Der Bot darf Fakten nur aus diesem Kontext nehmen. Findet die Suche nichts Passendes, gibt es keine gute Antwort.",
      en: "The bot may take facts only from this context. If the search finds nothing fitting, there is no good answer.",
    },
  },
  {
    id: "relevance",
    term: { de: "Relevanz-Score / Relevanz-Boden", en: "Relevance score / relevance floor" },
    what: {
      de: "Die Suche bewertet jede gefundene Seite von 0 bis 1. Der Relevanz-Boden (0,12 unter dem besten Treffer) entfernt Füllseiten, bevor sie das Modell sieht.",
      en: "The search scores every page found from 0 to 1. The relevance floor (0.12 below the best hit) removes filler pages before the model sees them.",
    },
    why: {
      de: "Hohe Scores bedeuten passende Quellen; viele Treffer unter dem Boden zeigen, dass der Index Rauschen liefert.",
      en: "High scores mean fitting sources; many hits below the floor show the index returns noise.",
    },
  },
  {
    id: "cited",
    term: { de: "Gefunden → weitergegeben → zitiert", en: "Found → forwarded → cited" },
    what: {
      de: "Der Retrieval-Trichter eines Turns: Dokumente, die die Suche fand; Seiten, die im Kontext des Modells landeten; Quellen, die die Antwort mit [n] wirklich zitierte.",
      en: "A turn's retrieval funnel: documents the search found; pages that landed in the model's context; sources the answer actually cited with [n].",
    },
    why: {
      de: "Zeigt, wo Qualität verloren geht — bei der Suche, beim Aufräumen oder beim Modell.",
      en: "Shows where quality is lost — in the search, in the clean-up, or in the model.",
    },
  },
  {
    id: "no-answer",
    term: { de: "Keine Antwort (no_answer / low_relevance)", en: "No answer (no_answer / low_relevance)" },
    what: {
      de: "Turns, bei denen die Suche nichts oder nur schwach Passendes fand und der Bot ehrlich «dazu habe ich keine Angaben» sagte.",
      en: "Turns where the search found nothing or only weak matches and the bot honestly said “I have no information on that”.",
    },
    why: {
      de: "Der direkteste Hinweis auf Inhaltslücken — ein Backlog fürs Content-Team.",
      en: "The most direct pointer to content gaps — a backlog for the content team.",
    },
  },
  {
    id: "question-check",
    term: { de: "Fragen-Check / Ergebnis", en: "Question check / result" },
    what: {
      de: "Eine eingefügte Liste echter Suchanfragen, gruppiert und als Testsitzungen durch die FAQ geschickt. Je Frage ein Ergebnis (Fehler, keine passende Seite, an den Kundendienst verwiesen, schwacher Treffer unter 0,7, ohne Quellenangabe, beantwortet) und die beste Seite mit ihrem Score.",
      en: "A pasted list of real search queries, grouped and sent through the FAQ as test sessions. Per question a result (error, no matching page, referred to customer service, weak hit below 0,7, no source cited, answered) and the best page with its score.",
    },
    why: {
      de: "Zeigt, wo das, was Leute wirklich suchen, keine oder nur eine schwache Seite findet — ein Backlog fürs Content-Team, bevor die Fragen im Chat auftauchen.",
      en: "Shows where what people actually search for finds no page or only a weak one — a backlog for the content team before the questions reach the chat.",
    },
  },
  {
    id: "streaming-failure",
    term: { de: "Stream-Abbruch", en: "Streaming failure" },
    what: {
      de: "Eine Antwort, die mitten in der Übertragung technisch abbrach (Zeitüberschreitung, Modellfehler).",
      en: "An answer that broke off technically midway through transmission (timeout, model error).",
    },
    why: {
      de: "Ein reines Technik-Signal: die Person hat eine halbe Antwort gesehen. Sollte nahe 0 bleiben.",
      en: "A pure technical signal: the person saw half an answer. Should stay near 0.",
    },
  },
  {
    id: "guardrail",
    term: { de: "Guardrail", en: "Guardrail" },
    what: {
      de: "Die Schutzprüfungen um jede Antwort: Entfernen persönlicher Daten (Telefon, E-Mail, AHV, IBAN), Sicherheitsfilter der Modellanbieter, Regeln im System-Prompt.",
      en: "The protective checks around every answer: removal of personal data (phone, e-mail, AHV, IBAN), the model providers' safety filters, rules in the system prompt.",
    },
    why: {
      de: "Jede Auslösung ist ein Vorfall, den man ansehen sollte — sie zeigt, wo Nutzende oder Inhalte an die Grenzen stossen.",
      en: "Every trigger is an incident worth a look — it shows where users or content push against the limits.",
    },
  },
  {
    id: "sanitizer",
    term: { de: "Sanitizer-Treffer", en: "Sanitizer hit" },
    what: {
      de: "Eine gefundene Quelle enthielt Text, der wie eine Anweisung an das Modell aussah («ignoriere alle Regeln») und vor der Weitergabe entfernt wurde.",
      en: "A source found contained text that looked like an instruction to the model (“ignore all rules”) and was removed before forwarding.",
    },
    why: {
      de: "Ein möglicher Manipulationsversuch über die Inhalte. Die Quelle gehört geprüft.",
      en: "A possible manipulation attempt through the content. The source deserves a check.",
    },
  },
  {
    id: "engaged",
    term: { de: "Vertiefte Session (≥3 Fragen)", en: "Engaged session (≥3 questions)" },
    what: {
      de: "Eine Session mit mindestens drei Fragen — dieselbe Definition in North Star, Engagement und KPI.",
      en: "A session with at least three questions — the same definition in North Star, Engagement and KPI.",
    },
    why: {
      de: "Unterscheidet echtes Dranbleiben von einem Klick. Die wichtigste Qualitätszahl der Nutzung.",
      en: "Separates real engagement from a click. The most important quality figure of use.",
    },
  },
  {
    id: "bounce",
    term: { de: "Absprung / Frühabbruch", en: "Bounce / early drop-off" },
    what: {
      de: "Eine Session mit höchstens einer Frage, auch ein gewählter Modus ohne Frage. Wer schon am Datenschutz-Hinweis oder im Startbildschirm aufhört, erzeugt keine Session und fehlt hier.",
      en: "A session with at most one question, a chosen mode without a question included. Someone who stops at the privacy notice or on the start screen creates no session and is missing here.",
    },
    why: {
      de: "Zeigt, wo das Angebot nicht zieht — oder wo der erste Schritt zu hoch ist.",
      en: "Shows where the offering does not pull — or where the first step is too high.",
    },
  },
  {
    id: "feedback",
    term: { de: "Feedback-Quote", en: "Feedback ratio" },
    what: {
      de: "Anteil Daumen hoch an allen abgegebenen Daumen-Bewertungen. Nur ein Bruchteil der Nutzenden bewertet.",
      en: "Share of thumbs-up among all thumbs ratings given. Only a fraction of users rate.",
    },
    why: {
      de: "Die direkteste Zufriedenheitszahl, aber eine kleine Stichprobe — immer mit der Anzahl Bewertungen daneben lesen.",
      en: "The most direct satisfaction figure, but a small sample — always read it with the number of ratings next to it.",
    },
  },
  {
    id: "k10",
    term: { de: "k≥10 / min_n", en: "k≥10 / min_n" },
    what: {
      de: "Anonymitäts-Schwelle: eine Zeile wird nur gezeigt, wenn mindestens 10 Personen (oder min_n) dahinterstehen.",
      en: "Anonymity threshold: a row is shown only when at least 10 people (or min_n) stand behind it.",
    },
    why: {
      de: "Alles, was das Haus verlässt, muss diese Schwelle einhalten — sonst wären Einzelpersonen rückidentifizierbar.",
      en: "Everything that leaves the building must respect this threshold — otherwise individuals could be re-identified.",
    },
  },
  {
    id: "intent",
    term: { de: "Absicht der Frage (Intent)", en: "Question intent" },
    what: {
      de: "Eine grobe Klassifikation, worum es in der Frage geht (Lehrstelle finden, Multicheck®, Bewerbung, Lohn, …), per Regex aus dem bereinigten Text. Eine Antwort auf eine Rückfrage des Chatbots ist keine Frage und zählt nicht mit.",
      en: "A coarse classification of what the question is about (finding an apprenticeship, Multicheck®, application, pay, …), by regex on the scrubbed text. A reply to a question of the chatbot is no question and does not count.",
    },
    why: {
      de: "Zeigt, wofür das Angebot genutzt wird. Trefferquote nur 30 bis 50 % — als Trend lesen, nicht pro Turn.",
      en: "Shows what the offering is used for. Recall only 30 to 50 % — read as a trend, not per turn.",
    },
  },
  {
    id: "reply-turn",
    term: { de: "Antwort auf eine Rückfrage", en: "Reply to the chatbot's question" },
    what: {
      de: "Eine Nachricht, die auf eine Frage des Chatbots antwortet («Gamen» auf «Was machst du gern?», «B» auf eine Aufgabe), statt selbst zu fragen. Die Absicht der Frage zählt sie nicht mit; ihre Zahl steht daneben.",
      en: "A message that answers a question of the chatbot («gaming» to «What do you like doing?», «B» to a task) instead of asking one. The question intent does not count it; its number shows beside it.",
    },
    why: {
      de: "Sonst sähen Modi mit vielen Rückfragen, etwa Orientierung oder Multicheck®, aus, als werde dort vor allem «Sonstiges» gefragt.",
      en: "Otherwise modes with many follow-up questions, such as Discovery or Multicheck®, would look as if people mostly asked «Other» there.",
    },
  },
  {
    id: "conversation-intent",
    term: { de: "Worum es in Gesprächen geht", en: "What conversations are about" },
    what: {
      de: "Die Absicht der ersten echten Frage eines Gesprächs, die das Titel-Modell zusammen mit dem Titel bestimmt: eine je Gespräch, dasselbe Vokabular wie die Absicht der Frage.",
      en: "The intent of a conversation's first real question, set by the title model together with the title: one per conversation, the same vocabulary as the question intent.",
    },
    why: {
      de: "Jedes Gespräch zählt einmal, ob lang oder kurz: zeigt, was Leute in den Chat bringt.",
      en: "Every conversation counts once, long or short: shows what brings people to the chat.",
    },
  },
  {
    id: "interest-term",
    term: { de: "Gefragte Begriffe / Trend", en: "Terms asked about / trend" },
    what: {
      de: "Die Begriffe, nach denen Gespräche fragen (Suchbegriffe des Titel-Modells, genannte Berufe, Stichworte), je Gespräch einmal gezählt. Der Trend vergleicht mit einem Zeitraum davor: neu, steigt oder sinkt.",
      en: "The terms conversations ask about (the title model's search terms, professions named, keywords), counted once per conversation. The trend compares with a period before: new, rising or falling.",
    },
    why: {
      de: "Zeigt früh, welche Themen aufkommen und wo Inhalte nachziehen müssen.",
      en: "Shows early which topics are emerging and where content has to catch up.",
    },
  },
  {
    id: "place-role",
    term: { de: "Wohnort / Suchgebiet / Erwähnt", en: "Home town / search area / mentioned" },
    what: {
      de: "Die Rolle eines Orts in einer Frage: wo die Person wohnt («ich wohne in Thun»), wo sie sucht («Lehrstelle in Bern») oder nur genannt.",
      en: "The role of a place in a question: where the person lives («I live in Thun»), where they search («apprenticeship in Bern») or just named.",
    },
    why: {
      de: "Trennt, woher Suchende kommen, von den Orten, wo sie Lehrstellen suchen: zwei verschiedene Fragen fürs Partner-Reporting.",
      en: "Separates where seekers come from and where they look for apprenticeships: two different questions for partner reporting.",
    },
  },
  {
    id: "daily-report",
    term: { de: "Tagesbericht", en: "Daily report" },
    what: {
      de: "Ein gespeicherter Bericht über einen Zürcher Tag: die Zahlen je Modus, von Code gezählt, und ein Text des Modells des Analyse-Chats dazu. Nachts um 03:00 Uhr für den Vortag, von Hand für jeden Tag der letzten 365 Tage; «vorläufig», solange der Tag läuft.",
      en: "A stored report about one Zurich day: the figures per mode, counted by code, and a text on them by the analysis chat's model. At 03:00 at night for the day before, by hand for any day of the last 365 days; «preliminary» while the day is running.",
    },
    why: {
      de: "Der tägliche Blick darauf, worum es ging, was neu ist und wo Antworten fehlten, ohne die Panels einzeln zu lesen.",
      en: "The daily look at what it was about, what is new and where answers were missing, without reading the panels one by one.",
    },
  },
  {
    id: "berufsfeld",
    term: { de: "Berufsfeld", en: "Occupational field" },
    what: {
      de: "Die übergeordnete Berufsgruppe (Technik & IT, Gesundheit, Wirtschaft, …), die aus der Frage aufgelöst wurde.",
      en: "The higher-level occupational group (technology & IT, health, business, …) resolved from the question.",
    },
    why: {
      de: "Die Achse für Nachfrage, Inhaltslücken und den Vergleich Interesse vs. Wahl.",
      en: "The axis for demand, content gaps and the interest-vs-choice comparison.",
    },
  },
  {
    id: "affect-traits",
    term: { de: "Stimmung / Merkmale", en: "Affect / traits" },
    what: {
      de: "Signale aus der Frage: Stimmung (positiv, frustriert, verwirrt, dringend, unsicher, Kraftausdruck) und Merkmale wie «interest:nature» oder «work:team».",
      en: "Signals from the question: mood (positive, frustrated, confused, urgent, unsure, profanity) and traits such as “interest:nature” or “work:team”.",
    },
    why: {
      de: "Nur als Aggregat verwenden — nie als Einordnung einer einzelnen Person.",
      en: "Use only as an aggregate — never as a judgement of an individual.",
    },
  },
  {
    id: "unattributed",
    term: { de: "Nicht zugeordnete Turns", en: "Unattributed turns" },
    what: {
      de: "Turns, deren Session vor dem gewählten Fenster begann. Ihr Partner ist nicht bekannt; sie zählen unter «Ohne Origin».",
      en: "Turns whose session started before the selected window. Their partner is unknown; they count under “No origin”.",
    },
    why: {
      de: "Erklärt, warum ein Partner-Total tiefer sein kann als erwartet — die Turns fehlen dort, statt falsch zugeordnet zu werden.",
      en: "Explains why a partner total can be lower than expected — the turns are missing there rather than credited wrongly.",
    },
  },
  {
    id: "privacy-wall",
    term: { de: "Datenschutz-Wand", en: "Privacy wall" },
    what: {
      de: "Der Datenschutz-Hinweis, der vor dem ersten Gespräch akzeptiert werden muss. Wer ihn sieht und nicht akzeptiert, ist dort «gestoppt». Eine Session entsteht erst nach der Zustimmung, deshalb zählt nur GA4 diese Abbrüche; das Dashboard zeigt sie nur für ältere Sessions, die vor der Zustimmung angelegt wurden.",
      en: "The privacy notice that must be accepted before the first conversation. Those who see it and do not accept are “stopped” there. A session starts only after the consent, so only GA4 counts these stops; the dashboard shows them only for older sessions created before the consent.",
    },
    why: {
      de: "Zeigt, wie viele der erste Schritt kostet — und ob die Formulierung Vertrauen schafft. Heute ist das eine GA4-Frage.",
      en: "Shows how many the first step costs — and whether the wording builds trust. Today that is a GA4 question.",
    },
  },
  {
    id: "return",
    term: { de: "Wiederkehr", en: "Return visit" },
    what: {
      de: "Näherung: eine Session, die über mehr als 6 Stunden aktiv war — derselbe Browser kam nach einer Pause zurück. Kein personenübergreifender Identifier.",
      en: "Approximation: a session active across more than 6 hours — the same browser came back after a pause. No cross-person identifier.",
    },
    why: {
      de: "Untergrenze für Bindung. Wer den Browser-Speicher löscht, erscheint als neue Session.",
      en: "A lower bound for retention. Anyone who clears browser storage appears as a new session.",
    },
  },
  {
    id: "commute",
    term: { de: "ÖV-Pendelzeit / BFS-Nummer", en: "Transit time / BFS number" },
    what: {
      de: "Fahrzeit mit dem öffentlichen Verkehr von Gemeinde zu Gemeinde (Richtwert, nicht von der Haustür). Die BFS-Nummer ist die amtliche Gemeinde-Nummer.",
      en: "Public-transport travel time from commune to commune (a guide value, not door to door). The BFS number is the official commune number.",
    },
    why: {
      de: "Grundlage der Lehrstellen-Rangfolge und der Talent-Geografie: wo Nachfrage ohne erreichbares Angebot besteht.",
      en: "The basis of the apprenticeship ranking and of talent geography: where demand exists without a reachable offer.",
    },
  },
  {
    id: "anomaly",
    term: { de: "Anomalie", en: "Anomaly" },
    what: {
      de: "Eine automatische Warnkarte, wenn eine Kennzahl ihre Schwelle überschreitet (zu viele «keine Antwort», Abbrüche, Fehler, tiefe Cache-Quote, bereinigte Inhalte).",
      en: "An automatic warning card when a metric crosses its threshold (too many “no answer”, aborts, errors, low cache rate, sanitized content).",
    },
    why: {
      de: "Der schnellste Blick auf «stimmt etwas nicht?» — ohne jede Tabelle lesen zu müssen.",
      en: "The fastest look at “is something wrong?” — without reading every table.",
    },
  },
  {
    id: "edge",
    term: { de: "Schutz am Rand (Cloud Armor)", en: "Edge protection (Cloud Armor)" },
    what: {
      de: "Die Schutzschicht vor der Anwendung: nur Anfragen aus CH, DE und AT kommen durch, dazu Rate-Limit pro IP und das Abfangen von Bot-Sonden.",
      en: "The protection layer in front of the application: only requests from CH, DE and AT get through, plus a per-IP rate limit and the interception of bot probes.",
    },
    why: {
      de: "Hält Missbrauch und Kosten fern, bevor sie die Anwendung erreichen. Die Health-Zeile edge_geo_header belegt, dass sie wirklich im Weg ist.",
      en: "Keeps abuse and cost away before they reach the application. The health row edge_geo_header proves it is actually in the path.",
    },
  },
  {
    id: "prompt-version",
    term: { de: "Prompt-Version", en: "Prompt version" },
    what: {
      de: "Die Versionsnummer der System-Prompts, die pro Turn gespeichert wird. Jede Textänderung an einem Prompt erhöht sie.",
      en: "The version number of the system prompts, stored per turn. Every text change to a prompt bumps it.",
    },
    why: {
      de: "Erlaubt, Qualitätsänderungen einer Prompt-Änderung zuzuordnen — vorher/nachher statt Bauchgefühl.",
      en: "Lets a quality change be attributed to a prompt change — before/after instead of gut feeling.",
    },
  },
];
